/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ListInputProps, MultiFileInputProps } from '../types/InputComponentProps';
import type { ParameterForm } from '@karya/parameter-specs';
import * as parameterSpecs from '@karya/parameter-specs';
const { flattenData, joiSchema, parameterArrayFromGroups, unflattenData } = parameterSpecs;
import Joi from 'joi';
import React, { ChangeEventHandler, FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { FormInputRenderer } from '../types/CustomRenderer';

/**
 * Describes the reason for a form change event.
 * Used to track and notify about different types of form state changes.
 *
 * @template T - The form data type
 * @template ChangedKey - The specific key that changed (for field-changed events)
 */
export type FormChangeReason<T, ChangedKey extends keyof T = keyof T> =
  | {
      cause: 'field-changed';
      fieldId: ChangedKey;
      previousValue: Partial<T>[ChangedKey];
      newValue: Partial<T>[ChangedKey];
    }
  | {
      cause: 'form-reset';
      previousForm: Partial<T>;
    }
  | {
      cause: 'form-set';
      previousForm: Partial<T>;
      newForm: Partial<T>;
    }
  | {
      cause: 'parameters-changed';
      previousForm: Partial<T>;
      newForm: Partial<T>;
    };

/**
 * Callback function that is invoked whenever the form state changes.
 * Provides information about what changed and access to the form control.
 *
 * @template T - The form data type
 * @param reason - Details about what caused the form change
 * @param control - The form control object with all form methods and state
 */
export type FormChangedHook<T> = (reason: FormChangeReason<T>, control: UseFormReturn<T>) => void;

/**
 * Information about a custom renderer component for form inputs.
 * Allows custom rendering of specific input types.
 *
 * @template Props - Props type for the renderer component
 * @template ExtraProps - Additional props type
 * @template FormState - Form state type
 */
export type RendererInfo<Props = Record<string, unknown>, ExtraProps = object, FormState = object> = {
  type: string;
  component: FormInputRenderer<Props, ExtraProps, FormState>;
  props: Props;
};

/**
 * Configuration options for the useForm hook.
 *
 * @template T - The form data type
 */
export type UseFormConfig<T> = {
  onSubmit?: (data: T) => void;
  formChangedHook?: FormChangedHook<T>;
  parameters: ParameterForm<T>;
  initialState?: Partial<T>;
  isKeyPath?: boolean;
  hideDisabledFields?: boolean;
  isEmptyStringValid?: boolean;
  sectionless?: boolean;
  renderers?: RendererInfo[];
  readonlyFields?: (keyof T)[];
  disabledFields?: (keyof T)[];
};

/**
 * Form context object that provides form state and handlers to child components.
 * This is typically passed down through React context for form rendering.
 *
 * @template T - The form data type
 */
export type FormContext<T> = {
  parameters: ParameterForm<T>;
  form: Partial<T>;
  errors: Partial<T>;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
  handleBooleanChange: ChangeEventHandler<HTMLInputElement>;
  handleFileChange: ChangeEventHandler<HTMLInputElement>;
  handleMultiFileChange: MultiFileInputProps['onChange'];
  handleJsonChange: ChangeEventHandler<HTMLInputElement>;
  handleListChange: ListInputProps['onChange'];
  setFormField: <Key extends keyof T>(id: Key, value: unknown) => void;
  setError: (id: keyof T, err: string) => void;
  renderers?: RendererInfo[];
  sectionless?: boolean;
  hideDisabledFields?: boolean;
  readonlyFields?: (keyof T)[];
  disabledFields?: (keyof T)[];
};

/**
 * Return value from the useForm hook.
 * Provides all form control methods and state access.
 *
 * @template T - The form data type
 */
export type UseFormReturn<T> = {
  ctx: FormContext<T>;
  validate: () => Joi.ValidationResult<T>;
  handleSubmit: FormEventHandler<HTMLFormElement>;
  setFormField: (id: keyof T, value: unknown) => void;
  setError: (id: keyof T, err: string) => void;
  resetForm: () => void;
  get: <Key extends keyof T>(id: Key, fallback?: T[Key]) => Partial<T>[Key];
  setForm: (newForm: Partial<T>) => void;
  setFormRaw: React.Dispatch<React.SetStateAction<Partial<T>>>;
  setParameterForm: React.Dispatch<React.SetStateAction<ParameterForm<T>>>;
};

/**
 * Custom React hook for managing form state, validation, and submission.
 *
 * This hook provides a complete form management solution with:
 * - Automatic validation using Joi schemas generated from parameter specifications
 * - Support for various input types (text, boolean, file, multi-file, JSON, list)
 * - Form change tracking and notifications
 * - Key path support for nested data structures (flattening/unflattening)
 * - Custom renderer support for specialized input components
 *
 * @template T - The form data type. Defaults to a generic object with string keys.
 *
 * @param config - Configuration object for the form hook
 * @param config.parameters - Parameter form specification defining structure and validation
 * @param config.onSubmit - Optional callback when form is submitted with valid data
 * @param config.formChangedHook - Optional callback for tracking form state changes
 * @param config.initialState - Optional initial form values
 * @param config.isKeyPath - If true, treats field IDs as dot-notation paths for nested data
 * @param config.hideDisabledFields - If true, hides disabled fields from rendering
 * @param config.isEmptyStringValid - If true, empty strings are valid; otherwise filtered before validation
 * @param config.sectionless - If true, renders form without sections/groups
 * @param config.renderers - Custom renderer components for specific input types
 * @param config.readonlyFields - Field IDs that should be read-only
 * @param config.disabledFields - Field IDs that should be disabled
 *
 * @returns Form control object with state, validation, and handler methods
 *
 * @example
 * ```tsx
 * const { ctx, handleSubmit, validate } = useForm({
 *   parameters: myParameterForm,
 *   onSubmit: (data) => {
 *     console.log('Form submitted:', data);
 *   },
 *   initialState: { name: 'John' },
 * });
 *
 * return (
 *   <form onSubmit={handleSubmit}>
 *     <input id="name" value={ctx.form.name} onChange={ctx.handleChange} />
 *     {ctx.errors.name && <span>{ctx.errors.name}</span>}
 *   </form>
 * );
 * ```
 */
export function useForm<T = { [id: string]: unknown }>(config: UseFormConfig<T>): UseFormReturn<T> {
  const { isKeyPath, onSubmit, formChangedHook } = config;

  const [parameterForm, setParameterForm] = useState<ParameterForm<T>>(config.parameters);
  const [form, setFormRaw] = useState<Partial<T>>(config.initialState ?? {});
  const [errors, setErrors] = useState<Partial<T>>({});
  const [formChangeInfo, setFormChangeInfo] = useState<FormChangeReason<T> | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  let parameterArray = parameterArrayFromGroups(parameterForm);
  if (config.disabledFields) {
    parameterArray = parameterArray.filter((param) => !config.disabledFields?.includes(param.id));
  }
  const schema = joiSchema<T>(parameterArray, { isKeyPath });

  const setFormField = useCallback(<Key extends keyof T>(id: Key, value: unknown) => {
    setFormRaw((previousForm) => {
      setFormChangeInfo({
        cause: 'field-changed',
        fieldId: id,
        previousValue: previousForm[id],
        newValue: value as T[Key],
      });
      return { ...previousForm, [id]: value };
    });
    setErrors((prevErrors) => {
      if (id in prevErrors) {
        const newErrors = { ...prevErrors };
        delete newErrors[id];
        return newErrors;
      }
      return prevErrors;
    });
    setIsDirty(true);
  }, []);

  useEffect(() => {
    let isFormUpdated = false;
    if (!isDirty) {
      if (config.initialState) {
        if (isKeyPath) {
          // @ts-expect-error - This is a hack to get around the type error
          setFormRaw(flattenData(parameterArray, config.initialState));
        } else {
          setFormRaw(config.initialState);
        }
        isFormUpdated = true;
      } else {
        parameterArray.forEach((param) => {
          if ('initial' in param && param.initial !== undefined) {
            setFormField(param.id, param.initial);
            isFormUpdated = true;
          }
        });
      }
    }

    if (isFormUpdated) {
      setIsDirty(true);
    }
  }, [parameterArray, config.disabledFields, config.initialState, isDirty, isKeyPath, setFormField]);

  const validate = useCallback(() => {
    let preValidationForm = form;

    if (!config.isEmptyStringValid) {
      const prevalidationFormEntries = Object.entries(form).filter(([_, value]) => value !== '');
      preValidationForm = Object.fromEntries(prevalidationFormEntries) as Partial<T>;
    }

    if (isKeyPath) {
      preValidationForm = unflattenData<T>(parameterArray, preValidationForm);
    }

    const result = schema.validate(preValidationForm, {
      stripUnknown: true,
    });

    const { error } = result;

    if (error) {
      const updatedErrors: Partial<T> = {};
      error.details.forEach((currError) => {
        const id = currError.path.join('.');
        let errorMessage = currError.message.replaceAll('"', '');

        // Replace pattern validation errors with simple "Invalid" message
        if (currError.type === 'string.pattern.base' || currError.type === 'string.pattern.invert.base') {
          errorMessage = 'Invalid Input';
        }

        updatedErrors[id] = errorMessage;
      });

      setErrors(updatedErrors);
    }

    return result;
  }, [form, config.isEmptyStringValid, isKeyPath, schema, parameterArray]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      const { value, error } = validate();
      if (error) {
        return;
      }
      setErrors({});
      if (onSubmit) {
        onSubmit(value);
      }
    },
    [validate, onSubmit],
  );

  const setError = useCallback((id: keyof T, err: string) => {
    setErrors((errors) => ({ ...errors, [id]: err }));
  }, []);

  const setForm = useCallback(
    (newForm: Partial<T>) => {
      setFormRaw((previousForm) => {
        const isFormChanged = !_.isEqual(previousForm, newForm);
        if (!isFormChanged) {
          return previousForm;
        }

        setFormChangeInfo({
          cause: 'form-set',
          previousForm,
          newForm,
        });

        if (isKeyPath) {
          return flattenData(parameterArray, newForm) as Partial<T>;
        } else {
          return newForm;
        }
      });
    },
    [isKeyPath, parameterArray],
  );

  useEffect(() => {
    if (isKeyPath) {
      const flattenedForm = flattenData(parameterArray, form);
      // HACK: Prevent infinite rerenders when the parameters are updated based on the form values
      if (!_.isEqual(flattenedForm, form)) {
        // @ts-expect-error - This is a hack to get around the type error
        setFormRaw((previousForm) => {
          setFormChangeInfo({
            cause: 'parameters-changed',
            previousForm,
            newForm: flattenedForm as Partial<T>,
          });
          return flattenedForm;
        });
      }
    }
  }, [parameterArray, form, isKeyPath]);

  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = useCallback(
    (e) => {
      const id = e.target.id as keyof T;
      const value = e.target.value;
      setFormField(id, value);
    },
    [setFormField],
  );

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const id = e.target.id as keyof T;
      const file = e.target.files?.[0];
      // Both "set file" and "clear file" go through setFormField
      setFormField(id, file ?? undefined);
    },
    [setFormField],
  );

  const handleMultiFileChange: MultiFileInputProps['onChange'] = useCallback(
    (id, files) => {
      setFormField(id as keyof T, files);
    },
    [setFormField],
  );

  const handleBooleanChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormField(e.target.id as keyof T, e.target.checked);
    },
    [setFormField],
  );

  const handleListChange: ListInputProps['onChange'] = useCallback(
    (id, values) => {
      setFormField(id as keyof T, values);
    },
    [setFormField],
  );

  const handleJsonChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const target_id = e.target.id as keyof T;
      if (e.target.files) {
        const file = e.target.files[0];
        const reader = new FileReader();
        reader.readAsText(file);
        reader.addEventListener(
          'load',
          () => {
            const resultJson = JSON.parse(reader.result as string);
            setFormField(target_id, resultJson);
          },
          false,
        );
      } else {
        setFormField(target_id, undefined);
      }
    },
    [setFormField],
  );

  const resetForm = useCallback(() => {
    setFormRaw((previousForm) => {
      setFormChangeInfo({
        cause: 'form-reset',
        previousForm,
      });
      return {};
    });
    setErrors({});
  }, []);

  const get = useCallback(
    <Key extends keyof T>(id: Key, fallback?: T[Key]) => {
      return form[id] ?? fallback;
    },
    [form],
  );

  const ctx = useMemo<UseFormReturn<T>['ctx']>(
    () => ({
      form,
      errors,
      handleChange,
      handleFileChange,
      handleMultiFileChange,
      handleJsonChange,
      handleBooleanChange,
      handleListChange,
      setFormField,
      setError,
      parameters: parameterForm,
      renderers: config.renderers ?? [],
      sectionless: config.sectionless,
      hideDisabledFields: config.hideDisabledFields,
      readonlyFields: config.readonlyFields,
      disabledFields: config.disabledFields,
    }),
    [
      config.disabledFields,
      config.hideDisabledFields,
      config.readonlyFields,
      config.renderers,
      config.sectionless,
      errors,
      form,
      handleBooleanChange,
      handleChange,
      handleFileChange,
      handleJsonChange,
      handleListChange,
      handleMultiFileChange,
      parameterForm,
      setError,
      setFormField,
    ],
  );

  const formControl = useMemo<UseFormReturn<T>>(
    () => ({
      ctx,
      validate,
      handleSubmit,
      resetForm,
      setFormField,
      setError,
      get,
      setForm,
      setFormRaw,
      setParameterForm,
    }),
    [ctx, get, handleSubmit, resetForm, setError, setForm, setFormField, validate],
  );

  useEffect(() => {
    if (formChangeInfo && formChangedHook) {
      formChangedHook(formChangeInfo, formControl);
      setFormChangeInfo(null);
    }
  }, [formChangeInfo, formChangedHook, formControl]);

  return formControl;
}
